import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import Header from "./Header";
import { formatHearingTime, TimezoneInfo } from "../utils/timezoneDisplay";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
  Card,
  CardHeader,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Input,
} from "@material-tailwind/react";
import { Link, useNavigate } from "react-router-dom";

interface Hearing {
  id: number;
  hearingDate: string;
  hearingTime: string;
  timezoneInfo?: TimezoneInfo;
  hearingType: {
    description: string;
  };
  state: {
    name: string;
    slug?: string;
  };
  district: {
    name: string;
  };
  court: string;
  processNumber: string;
  lawyerName: string;
  lawyerPhoneNumber: string;
  defendants: string[];
  plaintiff: string;
  lawFirm: string;
  virtualHearingLink?: string;
  notes?: string;
  status: string;
  cancellationNotice?: string;
  company?: {
    corporateName: string;
  };
  agentFee?: number;
  history?: string;
}

const Hearings: React.FC = () => {
  const navigate = useNavigate();
  const [hearings, setHearings] = useState<Hearing[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"invites" | "inProgress" | "previous">(
    "invites"
  );
  const [currentPage, setCurrentPage] = useState(1);

  const apiUri = import.meta.env.VITE_API_URI as string;

  useEffect(() => {
    const fetchHearings = async () => {
      try {
        const response = await api.get(`${apiUri}hearings/agent/list`);
        setHearings(response.data);
      } catch (err) {
        setError("Erro ao carregar audiências. Tente novamente mais tarde.");
      }
    };

    fetchHearings();
  }, []);

  // const renderHearing = (hearing: Hearing) => (
  //   <div key={hearing.id} className="border rounded-lg p-4 shadow-md">
  //     <h2 className="text-xl font-bold mb-2">{hearing.hearingType.description}</h2>
  //     <p><strong>Data:</strong> {new Date(hearing.hearingDate).toLocaleDateString()}</p>
  //     <p><strong>Hora:</strong> {hearing.hearingTime}</p>
  //     <p><strong>Modalidade:</strong> {hearing.hearingType.description}</p>
  //     <p><strong>Estado:</strong> {hearing.state.name}</p>
  //     <p><strong>Cidade:</strong> {hearing.district.name}</p>
  //     <p><strong>Vara:</strong> {hearing.court}</p>
  //     <p><strong>Processo:</strong> {hearing.processNumber}</p>
  //     <p><strong>Advogado:</strong> {hearing.lawyerName}</p>
  //     <p><strong>Telefone do Advogado:</strong> {hearing.lawyerPhoneNumber}</p>
  //     <p><strong>Réus:</strong> {Array.isArray(hearing.defendants) ? hearing.defendants.join(', ') : hearing.defendants}</p>
  //     <p><strong>Autor:</strong> {hearing.plaintiff}</p>
  //     <p><strong>Escritório de Advocacia:</strong> {hearing.lawFirm}</p>
  //     {hearing.virtualHearingLink && (
  //       <p><strong>Link da Audiência Virtual:</strong> <a href={hearing.virtualHearingLink} target="_blank" rel="noopener noreferrer">{hearing.virtualHearingLink}</a></p>
  //     )}
  //     {hearing.notes && <p><strong>Observações:</strong> {hearing.notes}</p>}
  //     <p><strong>Status:</strong> {hearing.status}</p>
  //     {hearing.cancellationNotice && <p><strong>Aviso de Cancelamento:</strong> {hearing.cancellationNotice}</p>}
  //     {hearing.company && <p><strong>Empresa:</strong> {hearing.company.corporateName}</p>}
  //     {hearing.agentFee && <p><strong>Taxa do Agente:</strong> {hearing.agentFee}</p>}
  //     {hearing.history && <p><strong>Histórico:</strong> {hearing.history}</p>}
  //     {view === 'invites' && (
  //       <button onClick={() => handleAcceptInvite(hearing.id)} className="mt-4 bg-blue-500 text-white px-4 py-2 rounded">
  //         Aceitar Convite
  //       </button>
  //     )}
  //   </div>
  // );

  const TABLE_HEAD = [
    "Data",
    "Hora",
    "Modalidade",
    "Estado",
    "Cidade",
    "Vara",
    "Processo",
    "Réus",
    "Autor",
    "Empresa",
    "Ver detalhes",
  ];

  const filteredHearings = hearings.filter((hearing) => {
    if (view === "invites") return hearing.status === "agendada";
    if (view === "inProgress") return hearing.status === "confirmada";
    if (view === "previous")
      return (
        hearing.status === "realizada" ||
        hearing.status === "finalizada" ||
        hearing.status === "cancelada"
      );
    return false;
  });

  const TABLE_ROWS = filteredHearings.map((hearing) => ({
    id: hearing.id,
    hearingDate: new Date(hearing.hearingDate).toString(),
    hearingTime: formatHearingTime(hearing),
    hearingType: hearing.hearingType.description,
    state: hearing.state.name,
    district: hearing.district.name,
    court: hearing.court,
    processNumber: hearing.processNumber,
    defendants: Array.isArray(hearing.defendants)
      ? hearing.defendants.join(", ")
      : hearing.defendants,
    plaintiff: hearing.plaintiff,
    company: hearing.company?.corporateName,
  }));

  const ITEMS_PER_PAGE = 50;
  const totalPages = Math.ceil(TABLE_ROWS.length / ITEMS_PER_PAGE);

  const currentTableData = TABLE_ROWS.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleHearingDetailsClick = (hearingDetails: Hearing) => {
    navigate(`/hearing-details/${hearingDetails.id}`, {
      state: { hearingDetails },
    });
    console.log("hearings:", hearingDetails);
  };

  return (
    <>
      <Header />
      <div className="container mx-auto p-4">
        {error && (
          <div className="flex items-center justify-center mt-12">
            <p className="text-red-600 bg-red-50 p-4 rounded-md max-w-fit ml-8 mr-8 mx-auto text-sm">
              {error}
            </p>
          </div>
        )}
        <div className="flex justify-center mt-2 text-preppoBlue text-medium space-x-1">
          <Button
            onClick={() => setView("invites")}
            className={`px-4 py-2 ${
              view === "invites"
                ? "bg-preppoBlue text-white"
                : "text-preppoBlue bg-white"
            }`}
            placeholder={undefined}
          >
            Convites pendentes
          </Button>
          <Button
            onClick={() => setView("inProgress")}
            className={`px-4 py-2 ${
              view === "inProgress"
                ? "bg-preppoBlue text-white"
                : "text-preppoBlue bg-white"
            }`}
            placeholder={undefined}
          >
            Em progresso
          </Button>
          <Button
            onClick={() => setView("previous")}
            className={`px-4 py-2 ${
              view === "previous"
                ? "bg-preppoBlue text-white"
                : "text-preppoBlue bg-white"
            }`}
            placeholder={undefined}
          >
            Antigas
          </Button>
        </div>
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hearings
            .filter(hearing => {
              if (view === 'invites') return hearing.status === 'agendada';
              if (view === 'inProgress') return hearing.status === 'confirmada';
              if (view === 'previous')
                return (
                  hearing.status === 'realizada' ||
                  hearing.status === 'finalizada' ||
                  hearing.status === 'cancelada'
                );
              return false;
            })
            .map(renderHearing)}
        </div> */}
      </div>
      <Card
        className="h-full max-w-full p-2 mx-2 text-preppoBlue"
        placeholder={undefined}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className="rounded-none"
          placeholder={undefined}
        >
          <div className="mb-2 flex flex-col justify-between gap-8 md:flex-row md:items-center">
            <div>
              <Typography
                variant="h5"
                className="text-preppoBlue"
                placeholder={undefined}
              >
                Audiências
              </Typography>
            </div>
            {/* <div className="flex w-full shrink-0 gap-2 md:w-max">
            <div className="w-full md:w-72">
              <Input
                  label="Pesquisar"
                  icon={<MagnifyingGlassIcon className="h-5 w-5" />}   crossOrigin={undefined}              />
            </div>
            <Button className="flex items-center gap-3" size="sm" placeholder={undefined}  >
              <ArrowDownTrayIcon strokeWidth={2} className="h-4 w-4" /> Download
            </Button>
          </div> */}
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-0" placeholder={undefined}>
          <table className="w-full min-w-max table-auto text-left rounded-lg overflow-hidden">
            <thead>
              <tr>
                {TABLE_HEAD.map((head) => (
                  <th
                    key={head}
                    className="border-y border-blue-gray-100 bg-preppoBlue p-4"
                  >
                    <Typography
                      variant="small"
                      className="font-normal leading-none text-white"
                      placeholder={undefined}
                    >
                      {head}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentTableData.map(
                (
                  {
                    id,
                    hearingDate,
                    hearingTime,
                    hearingType,
                    state,
                    district,
                    court,
                    processNumber,
                    defendants,
                    plaintiff,
                    company,
                  },
                  index
                ) => {
                  const isLast = index === TABLE_ROWS.length - 1;
                  const classes = isLast
                    ? "p-4"
                    : "p-4 border-b border-blue-gray-50";

                  return (
                    <tr
                      key={id}
                      className="hover:bg-preppoBlue-light"
                      onClick={() =>
                        handleHearingDetailsClick(filteredHearings[index])
                      }
                    >
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {hearingDate}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {hearingTime}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {hearingType}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {state}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {district}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {court}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {processNumber}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {defendants}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {plaintiff}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                          placeholder={undefined}
                        >
                          {company}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Link
                          to={`/hearings/${id}`}
                          className="flex justify-center items-center text-preppoBlue hover:underline"
                        >
                          <MagnifyingGlassIcon className="h-5 w-5 mr-1" />
                        </Link>
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </CardBody>
        <CardFooter
          className="flex items-center justify-between border-t border-pre p-4"
          placeholder={undefined}
        >
          <Button
            variant="outlined"
            size="sm"
            placeholder={undefined}
            onClick={handlePreviousPage}
          >
            Anterior
          </Button>
          <div className="flex items-center gap-2">
            {[...Array(totalPages)].map((_, index) => (
              <IconButton
                key={index}
                variant={currentPage === index + 1 ? "outlined" : "text"}
                size="sm"
                placeholder={undefined}
                onClick={() => handlePageClick(index + 1)}
              >
                {index + 1}
              </IconButton>
            ))}
          </div>
          <Button
            variant="outlined"
            size="sm"
            placeholder={undefined}
            onClick={handleNextPage}
          >
            Próxima
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};

export default Hearings;
